<script async setup>
import { defineProps, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { tns } from "tiny-slider";

const props = defineProps({
    elementId: {
        type: [Number, String],
        required: true
    },
});

const sliderApi = ref(null);
const slideCount = ref(0);

const initSlider = async () => {
    await nextTick();
    sliderApi.value = tns({
        container: `#slider-${props.elementId}`,
        items: 4,
        slideBy: 1,
        gutter: 20,
        controls: false,
        nav: false,
        mouseDrag: false,
        speed: 400,
        loop: false,
        rewind: true,
        center: false,
        edgePadding: 10,
        responsive: {
            0: { items: 1, fixedWidth: 280, edgePadding: 0 },
            680: { fixedWidth: false, items: 2, gutter: 30 },
            980: { items: 3, gutter: 20 },
            1340: { items: 4, gutter: 20 },
        },
    });

    const info = sliderApi.value.getInfo?.();
    slideCount.value = info?.slideCount ?? 0;
};

const destroySlider = () => {
    sliderApi.value?.destroy();
    sliderApi.value = null;
};

const prevSlide = () => sliderApi.value?.goTo("prev");
const nextSlide = () => sliderApi.value?.goTo("next");

let lastIsSmall = null;
let resizeRaf = 0;

const handleResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
        const isSmall = window.innerWidth < 680;

        if (lastIsSmall === null) {
            lastIsSmall = isSmall;
            return;
        }
        if (isSmall !== lastIsSmall) {
            lastIsSmall = isSmall;
            destroySlider();
            initSlider().then(() => {
                sliderApi.value?.goTo(0);
            });
        } else {
            sliderApi.value?.refresh?.();
        }
    });
};

onMounted(async () => {
    await initSlider();
    lastIsSmall = window.innerWidth < 680;
    window.addEventListener("resize", handleResize, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    destroySlider();
});
</script>

<template>
    <div class="relative">
        <div class="overflow-hidden">
            <div :id="'slider-' + props.elementId">
                <slot />
            </div>
        </div>

        <div v-if="slideCount > 4" class="max-w-ts-content mx-auto relative">
            <div class="flex justify-end mt-4">
                <div class="flex gap-x-4">
                    <button class="cursor-pointer" @click="prevSlide">
                        <kd-icon-arrow-left class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                    <button class="cursor-pointer" @click="nextSlide">
                        <kd-icon-arrow-right class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
